import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

import shouldConsiderCatchupAsLive from 'services/data-manager/parsing/utils/shouldConsiderCatchupAsLive.ts';
import { VideoItem } from 'services/data-manager/types.ts';

import usePrevious from 'utils/hooks/usePrevious.ts';
import useUpdateEffect from 'utils/hooks/useUpdateEffect.ts';

import { PlayerRef } from 'components/Player/types.ts';

export type Params = {
    videoItem: VideoItem | undefined;
    isLive: boolean;
    isCatchup: boolean;
    isStartover: boolean;
    playerPaused: boolean;
    playerRef: RefObject<PlayerRef | null>;
    playerProgress: number;
    sliderIsActive: boolean;
};

function useLivePosition({
    videoItem,
    isLive,
    isCatchup,
    isStartover,
    playerPaused,
    playerProgress,
    playerRef,
    sliderIsActive,
}: Params) {
    const interval = useRef<NodeJS.Timeout>(undefined);

    const [livePosition, setLivePostition] = useState<undefined | number>(
        undefined,
    );

    const prevPlayerProgress = usePrevious(playerProgress);

    const handleResetLivePosition = useCallback(() => {
        setLivePostition(undefined);
    }, []);

    useUpdateEffect(() => {
        if (!sliderIsActive) {
            (async () => {
                const notFinishedCatchup =
                    isCatchup &&
                    shouldConsiderCatchupAsLive(videoItem?.endTime);

                if (
                    isLive &&
                    prevPlayerProgress &&
                    playerProgress > prevPlayerProgress
                ) {
                    setLivePostition(playerProgress);
                } else if (
                    (isStartover || notFinishedCatchup) &&
                    (await playerRef.current?.getIsLive()) === false
                ) {
                    const duration = await playerRef.current?.getDuration();

                    if (typeof duration === 'number') {
                        setLivePostition(Math.floor(duration));
                    }
                } else if (
                    (isStartover || notFinishedCatchup) &&
                    (await playerRef.current?.getIsLive())
                ) {
                    const maxTimeshift =
                        await playerRef.current?.getMaxTimeShift();

                    if (typeof maxTimeshift === 'number') {
                        setLivePostition(Math.abs(maxTimeshift));
                    }
                }
            })();
        }
    }, [playerProgress]);

    useEffect(() => {
        if (isStartover) {
            setLivePostition((prevLivePosition) =>
                videoItem?.guardTime != null
                    ? Math.max(
                          0,
                          (prevLivePosition || 0) + videoItem?.guardTime,
                      )
                    : Math.max(0, prevLivePosition || 0),
            );
        }
    }, [isStartover]);

    useEffect(() => {
        const notFinishedCatchup =
            isCatchup && shouldConsiderCatchupAsLive(videoItem?.endTime);

        if ((isLive || isStartover || notFinishedCatchup) && playerPaused) {
            if (interval.current) {
                clearInterval(interval.current);
                interval.current = undefined;
            }

            interval.current = setInterval(() => {
                setLivePostition((prevLivePosition) =>
                    prevLivePosition != null
                        ? prevLivePosition + 1
                        : prevLivePosition,
                );
            }, 1000);
        }

        return () => {
            if (interval.current) {
                clearInterval(interval.current);
                interval.current = undefined;
            }
        };
    }, [isLive, isStartover, playerPaused]);

    return {
        livePosition,
        onResetLivePosition: handleResetLivePosition,
    };
}

export default useLivePosition;
